import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';

const ONBOARDING_STEPS = [
  {
    icon: '/spot-note.png',
    title: 'Master Sight Reading',
    description: 'Glissando helps you improve your sight-reading ability through drills that progressively get harder as you master notes.'
  },
  {
    icon: '/spot-target.png',
    title: 'Play the Right Key',
    description: 'Identify the note shown on the musical staff and then play the piano key on the keyboard below the staff.'
  },
  {
    icon: '/spot-clock.png',
    title: 'Challenge Yourself',
    description: 'You can choose to time yourself, playing as many notes as you can in 60 seconds and try to beat your record.'
  },
  {
    icon: '/spot-streak.png',
    title: 'Build Your Streak',
    description: 'Practice daily to build your mastery. Notes you struggle with will appear more often until you get them down.'
  }
];

const OnboardingOverlay = memo(function OnboardingOverlay({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  
  const handleNext = () => {
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onComplete();
    }
  };
  
  const handleSkip = () => {
    onComplete();
  };
  
  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  
  return (
    <div className="onboarding-overlay" data-testid="onboarding-overlay">
      <motion.div 
        className="onboarding-card"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {ONBOARDING_STEPS.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index <= currentStep 
                  ? 'bg-coral-500' 
                  : 'bg-cream-300'
              }`}
            />
          ))}
        </div>
        
        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="text-center"
          >
            {/* Icon Image */}
            <div className="flex justify-center mb-8">
              <img 
                src={step.icon} 
                alt="" 
                className="w-28 h-28 object-contain"
              />
            </div>
            
            {/* Title */}
            <h2 className="font-serif text-2xl text-cream-900 mb-4">
              {step.title}
            </h2>
            
            {/* Description */}
            <p className="font-sans text-wood-700 text-base leading-relaxed mb-8 px-2">
              {step.description}
            </p>
          </motion.div>
        </AnimatePresence>
        
        {/* Actions */}
        <div className="flex flex-col gap-4 mt-auto">
          <Button
            onClick={handleNext}
            className="w-full bg-coral-500 hover:bg-coral-600 active:bg-coral-700 text-white font-sans font-medium py-6 rounded-btn text-base transition-colors"
            data-testid="onboarding-next-btn"
          >
            {isLastStep ? "Let's Begin!" : 'Continue'}
          </Button>
          
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="text-sm text-wood-500 hover:text-wood-700 font-sans transition-colors py-2"
              data-testid="onboarding-skip-btn"
            >
              Skip Introduction
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
});

export default OnboardingOverlay;
